/* eslint-disable complexity */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import {
  isPending,
  ROUTES,
  Logger,
  CognitoUser,
  useLatestVersionDetection,
  APIClients,
  ApiClientType,
  IntegrationsAPIClient
} from '@futrli/shared-services';

import { resetError } from '../../core/redux-utils/actions';
import { SIGN_IN } from '../constants';
import { Storage } from '../../storage/storage';
import { signIn, signInStarter } from '../redux-utils/actions';
import { getErrorSelector } from '../../core/redux-utils/selectors';
import { AuthError, StoreState } from '../../core/types';
import { GET_CURRENT_AUTHENTICATED_USER } from '../../auth/constants';
import { useFederatedSignIn } from '../../federated-sign-in/use-federated-sign-in';
import { useGetRedirectInfoFromQueryParam } from '../../auth/components/use-get-redirect-info-from-query-param';
import { SESSION_STORAGE_REDIRECT_PATH } from '../../auth/containers/auth-container';
import { isProdEnvironment } from '../../../../skeleton/src/utils/env-utils';
import { trackEvent } from '../../../../predict/src/core/tracker';
import { TRACKING_EVENTS } from '../../../../predict/src/core/constants';
import { LoginForm } from './login-form';
import { MfaForm } from './mfa-form';
import { clearLocalStorageWithExceptions } from '@futrli/shared-services/string-utils/utils';

interface Props {
  sageIdEmail?: string;
}

type LoginFormData = Partial<{
  username: string;
  password: string;
}>;

/**
 * Login user page.
 */
export const Login = ({ sageIdEmail }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();
  const isSignInLoading = useSelector((state: StoreState) => isPending(state.loadingRequests.SIGN_IN));
  const isCurrentAuthenticatedUserLoading = useSelector((state: StoreState) =>
    isPending(state.loadingRequests.GET_CURRENT_AUTHENTICATED_USER)
  );
  const error = useSelector((state: StoreState) => getErrorSelector(state.authReducers, SIGN_IN.ACTION));
  const { isFederatedSignInLoading, federatedSignInError, handleFederatedSignIn } = useFederatedSignIn();
  const { isLoadingRedirectInfo, redirectInfo, onRedirect } = useGetRedirectInfoFromQueryParam();
  const [checkLoginError, setCheckLoginError] = useState<AuthError | null>(null);

  const [showMfaForm, setShowMfaForm] = useState(false);
  const [starterLoading, setStarterLoading] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState<CognitoUser | null>(null);

  const isLoading =
    isSignInLoading ||
    isFederatedSignInLoading ||
    isCurrentAuthenticatedUserLoading ||
    isLoadingRedirectInfo ||
    starterLoading;

  React.useEffect(() => {
    APIClients.setApiClient(ApiClientType.Aws);
    IntegrationsAPIClient.setApiClient(ApiClientType.Aws);

    clearLocalStorageWithExceptions();
    return () => {
      dispatch(resetError(SIGN_IN));
      dispatch(resetError(GET_CURRENT_AUTHENTICATED_USER));
    };
  }, [dispatch]);

  useLatestVersionDetection(true);

  const handleRedirect = async () => {
    const redirectPath = sessionStorage.getItem(SESSION_STORAGE_REDIRECT_PATH);

    if (redirectPath) {
      sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_PATH);
      navigate(redirectPath);
    } else if (redirectInfo) {
      return await onRedirect();
    } else {
      navigate(ROUTES.ROOT_ROUTE);
    }
  };

  const handleSubmit = async ({ username, password }: LoginFormData) => {
    if (!username || !password) {
      return;
    }
    try {
      setStarterLoading(true);
      const signInStarterResponse = await signInStarter(username, password);

      if (signInStarterResponse.isMfa) {
        setShowMfaForm(true);
        setLoggedInUser(signInStarterResponse.user);
      } else {
        dispatch(signIn(navigate, username, password, handleRedirect));
        trackEvent(TRACKING_EVENTS.LOGGED_IN);
      }
    } catch (e) {
      if (isProdEnvironment()) {
        window.utag?.link({
          event: 'form',
          event_name: 'form_submit_error',
          form_name: 'login'
        });
      }
      Logger.error(e as string);
      setCheckLoginError({
        code: 'NotAuthorizedException',
        message: intl.formatMessage({
          id: 'login.incorrect_username_or_password',
          defaultMessage: 'Incorrect username or password.'
        }),
        name: 'NotAuthorizedException'
      });
    } finally {
      setStarterLoading(false);
    }
  };

  const username = Storage.getInstance().get('username', '') || sageIdEmail || '';

  if (showMfaForm) {
    return <MfaForm loggedInUser={loggedInUser} />;
  }

  return (
    <LoginForm
      disabledEmail={!!sageIdEmail}
      initialUsername={username}
      isLoading={isLoading}
      cognitoSignInError={error || checkLoginError}
      federatedSignInError={federatedSignInError}
      onFederatedSignIn={handleFederatedSignIn}
      onSubmit={handleSubmit}
    />
  );
};
